import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart3, Download, Search } from "lucide-react";

const PERIODS = [["hourly", "Hourly · 24h"], ["daily", "Daily · 30d"], ["weekly", "Weekly · 12w"]];
const PAGE = 25;
const fmtCT = (iso) => iso ? new Date(iso).toLocaleString("en-US", { timeZone: "America/Chicago", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";
const prettyType = (t) => (t || "").replace(/_/g, " ");
const STATUS_CLS = { sent: "bg-emerald-50 text-emerald-700 border-emerald-200", failed: "bg-red-50 text-red-700 border-red-200", simulated: "bg-slate-50 text-slate-600 border-slate-200" };

const Stat = ({ label, value, testid, cls = "text-slate-900" }) => (
  <div className="rounded-lg border border-slate-100 bg-slate-50/60 px-4 py-3">
    <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
    <div data-testid={testid} className={`font-mono-metric text-2xl font-bold ${cls}`}>{value ?? 0}</div>
  </div>
);

function ActivityChart() {
  const [period, setPeriod] = useState("daily");
  const { data } = useQuery({ queryKey: ["sms-activity", period], queryFn: async () => (await api.get("/sms/activity", { params: { period } })).data });
  const t = data?.totals || {};
  return (
    <div className="p-6 border-b border-slate-100">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50">
          {PERIODS.map(([k, l]) => (
            <button key={k} data-testid={`sms-activity-period-${k}`} onClick={() => setPeriod(k)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${period === k ? "bg-[#0B1F44] text-white" : "text-slate-600 hover:text-slate-900"}`}>{l}</button>
          ))}
        </div>
        <span className="text-[11px] text-slate-400">Times shown in US Central</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Stat label="Total outbound" value={t.total} testid="sms-stat-total" />
        <Stat label="Delivered to carrier" value={t.sent} testid="sms-stat-sent" cls="text-emerald-600" />
        <Stat label="Failed / blocked" value={t.failed} testid="sms-stat-failed" cls="text-red-600" />
        <Stat label="Simulated" value={t.simulated} testid="sms-stat-simulated" cls="text-slate-500" />
      </div>
      <div className="h-64" data-testid="sms-activity-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data?.buckets || []} margin={{ left: -20, right: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#64748b" }} interval="preserveStartEnd" />
            <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#64748b" }} />
            <Tooltip cursor={{ fill: "#f1f5f9" }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="sent" stackId="a" name="Sent" fill="#0B1F44" />
            <Bar dataKey="failed" stackId="a" name="Failed / blocked" fill="#ef4444" />
            <Bar dataKey="simulated" stackId="a" name="Simulated" fill="#cbd5e1" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      {data?.by_type?.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4" data-testid="sms-by-type">
          {data.by_type.map((x) => (
            <span key={x.type} className="text-xs px-2.5 py-1 rounded-full border border-slate-200 bg-white text-slate-700 capitalize">
              {prettyType(x.type)} <b className="font-mono-metric">{x.count}</b>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function SentLog() {
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(0);
  useEffect(() => { const h = setTimeout(() => { setQ(search); setPage(0); }, 350); return () => clearTimeout(h); }, [search]);
  const params = { search: q, status: status === "all" ? "" : status };
  const { data } = useQuery({ queryKey: ["sms-log", q, status, page], queryFn: async () => (await api.get("/sms/log", { params: { ...params, skip: page * PAGE, limit: PAGE } })).data });
  const total = data?.total || 0;
  const exportCsv = async () => {
    try {
      const res = await api.get("/sms/log/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "sms_sent_log.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };
  return (
    <div className="p-6">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="sms-log-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search recipient or message…" className="h-9 pl-9" />
        </div>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(0); }}>
          <SelectTrigger data-testid="sms-log-status-filter" className="h-9 w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="failed">Failed / blocked</SelectItem>
            <SelectItem value="simulated">Simulated</SelectItem>
          </SelectContent>
        </Select>
        <Button data-testid="sms-log-export" variant="outline" className="h-9" onClick={exportCsv}><Download className="h-4 w-4 mr-1.5" />Export CSV</Button>
      </div>
      <div className="overflow-x-auto border border-slate-100 rounded-lg">
        <table className="w-full text-sm" data-testid="sms-log-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr><th className="text-left px-4 py-2">Sent</th><th className="text-left px-4 py-2">Recipient</th><th className="text-left px-4 py-2">Type</th><th className="text-left px-4 py-2">Status</th><th className="text-left px-4 py-2">Message</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {(data?.items || []).map((m) => (
              <tr key={m.id} data-testid={`sms-log-row-${m.id}`}>
                <td className="px-4 py-2 whitespace-nowrap text-slate-600">{fmtCT(m.created_at)}</td>
                <td className="px-4 py-2 whitespace-nowrap font-medium text-slate-800">{m.recipient}</td>
                <td className="px-4 py-2 whitespace-nowrap capitalize text-slate-600">{prettyType(m.type)}</td>
                <td className="px-4 py-2"><span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${STATUS_CLS[m.status] || STATUS_CLS.simulated}`}>{m.status}</span></td>
                <td className="px-4 py-2 text-slate-600 max-w-md truncate" title={m.body}>{m.body}</td>
              </tr>
            ))}
            {total === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400">No outgoing texts found.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
        <span data-testid="sms-log-count">{total} message(s)</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" data-testid="sms-log-prev" disabled={page === 0} onClick={() => setPage(page - 1)}>Prev</Button>
          <Button size="sm" variant="outline" data-testid="sms-log-next" disabled={(page + 1) * PAGE >= total} onClick={() => setPage(page + 1)}>Next</Button>
        </div>
      </div>
    </div>
  );
}

export const SmsActivity = () => (
  <div data-testid="sms-activity-card" className="bg-white border border-slate-200 rounded-xl shadow-sm mb-6 overflow-hidden">
    <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
      <BarChart3 className="h-4 w-4 text-cyan-500" />
      <h3 className="font-heading font-bold text-slate-900">SMS Activity — Sent Messages</h3>
    </div>
    <ActivityChart />
    <SentLog />
  </div>
);
